import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { TherapistAvatar } from 'components/ui/therapist-avatar';
import { fetchIntakeProfile, IntakeProfile } from '../../api/intake';
import { AppointmentModal } from '../appointment-modal';
import { Therapist } from '../../types/user';
import { ArrowLeft } from 'lucide-react';


export const IntakeProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<IntakeProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBooking, setShowBooking] = useState(false);
  const [bookingTherapist, setBookingTherapist] = useState<Therapist | null>(null);

  useEffect(() => {
    fetchIntakeProfile()
      .then((p) => {
        if (p.status === 'completed') {
          setProfile(p);
        } else {
          // Not completed — redirect to intake wizard
          navigate('/intake', { replace: true });
        }
      })
      .catch(() => {
        setError(t('intake.noProfileYet'));
      })
      .finally(() => setIsLoading(false));
  }, [navigate, t]);

  const handleBook = (therapist: any) => {
    const th: Therapist = {
      id: therapist?.userId || therapist?.id || '',
      userId: therapist?.userId || therapist?.id || '',
      email: '',
      firstName: therapist?.user?.firstName || therapist?.firstName || '',
      lastName: therapist?.user?.lastName || therapist?.lastName || '',
      type: 'therapist',
      imageUrl: therapist?.imageUrl || '',
      name: therapist?.name || `Dr. ${therapist?.user?.firstName || ''}`,
      bio: therapist?.bio || '',
      specialties: therapist?.specialties || [],
      rating: therapist?.rating || 0,
      yearsExperience: therapist?.yearsExperience || 0,
      nextAvailable: therapist?.nextAvailable || '',
      languages: therapist?.languages || [],
    };
    setBookingTherapist(th);
    setShowBooking(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-10 h-10 rounded-full border-4 border-[#00c4cc] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !profile?.aiProfile) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-gray-500">{error || t('intake.noProfileYet')}</p>
        <Button
          onClick={() => navigate('/intake')}
          className="text-white"
          style={{ backgroundColor: '#00c4cc' }}
        >
          {t('intake.startIntake')}
        </Button>
      </div>
    );
  }

  const ai = profile.aiProfile;
  const matches = profile.matches || [];
  const therapists = profile.therapists || [];
  const topMatch = matches[0];
  const topTherapist = topMatch
    ? therapists.find(
        (th: any) => th.userId === topMatch.therapistId || th.id === topMatch.therapistId
      )
    : null;

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00c4cc] to-[#008ba3] text-white pt-10 pb-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('intake.back')}
          </button>
          <h1 className="text-3xl font-bold">{t('intake.myProfileTitle')}</h1>
          <p className="text-white/80 mt-2">{t('intake.myProfileSubtitle')}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-8 -mt-10 space-y-6">
        {/* 1. Cultural context — ground the page in the patient's story */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-2">{t('intake.revealContext')}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{ai.culturalContext}</p>
        </div>

        {/* 2. Key insight + stressor themes (merged) */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-2">{t('intake.revealInsightTitle')}</h3>
          <p className="text-gray-600 text-sm leading-relaxed">{ai.keyInsight}</p>
          <hr className="my-4 border-gray-100" />
          <h4 className="font-semibold text-gray-800 text-sm mb-3">{t('intake.revealThemes')}</h4>
          <div className="flex flex-wrap gap-2">
            {ai.stressorThemes.map((theme, i) => (
              <span
                key={i}
                className="px-3 py-1.5 bg-cyan-50 text-[#00c4cc] rounded-full text-sm font-medium"
              >
                {theme}
              </span>
            ))}
          </div>
        </div>

        {/* 4. Therapist match + other matches */}
        {topMatch && topTherapist && (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 text-center text-lg">
              {t('intake.revealRecommendation')}
            </h3>
            <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-[#00c4cc]/20" data-testid="therapist-match-card">
              <div className="flex items-start gap-4">
                <TherapistAvatar imageUrl={topTherapist?.imageUrl} name={topTherapist?.name} size="md" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-800">
                    {topTherapist?.name ||
                      (topTherapist?.firstName || topTherapist?.user?.firstName
                        ? `Dr. ${topTherapist.firstName || topTherapist.user?.firstName} ${topTherapist.lastName || topTherapist.user?.lastName || ''}`.trim()
                        : t('intake.revealTherapist'))}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-[#00c4cc] font-semibold">
                      {Math.round(topMatch.score * 100)}% {t('intake.revealMatch')}
                    </span>
                  </div>
                  {topTherapist?.specialties && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {topTherapist.specialties.slice(0, 3).map((s: any) => (
                        <span
                          key={s.id || s.name}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                        >
                          {s.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-3 text-gray-600 text-sm leading-relaxed">
                {topMatch.explanation}
              </p>
              <Button
                onClick={() => handleBook(topTherapist)}
                className="w-full mt-4 text-white"
                style={{ backgroundColor: '#00c4cc' }}
              >
                {t('intake.revealBook')}
              </Button>
            </div>

            {/* Other matches */}
            {matches.length > 1 && (
              <div className="space-y-3">
                <p className="text-center text-gray-500 text-sm">
                  {t('intake.revealOthers')}
                </p>
                {matches.slice(1).map((match) => {
                  const th = therapists.find(
                    (th: any) => th.userId === match.therapistId || th.id === match.therapistId
                  );
                  return (
                    <div
                      key={match.therapistId}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        <TherapistAvatar imageUrl={th?.imageUrl} name={th?.name} size="xs" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-800 text-sm">
                            {th?.name ||
                              (th?.firstName || th?.user?.firstName
                                ? `Dr. ${th.firstName || th.user?.firstName}`
                                : `Therapist #${match.rank}`)}
                          </p>
                          <p className="text-xs text-[#00c4cc]">
                            {Math.round(match.score * 100)}% {t('intake.revealMatch')}
                          </p>
                        </div>
                      </div>
                      <p className="mt-2 text-gray-500 text-xs">{match.explanation}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 5. Educational content */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-800 text-center text-lg">
            {t('intake.eduTitle')}
          </h3>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-purple-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">💜</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduWhyTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduWhyText')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🤝</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduPrepareTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduPrepareText')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-green-100">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🌍</span>
              <div>
                <h4 className="font-semibold text-gray-800 text-sm">{t('intake.eduNotAloneTitle')}</h4>
                <p className="text-gray-600 text-sm mt-1">{t('intake.eduNotAloneText')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking modal */}
      {bookingTherapist && (
        <AppointmentModal
          isOpen={showBooking}
          onClose={() => setShowBooking(false)}
          therapist={bookingTherapist}
        />
      )}
    </div>
  );
};

export default IntakeProfilePage;
